import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';
import { selectSelectedStore } from '../../user-access/user-stores-store/user-store.selectors';
import { isStockTakeBlockedBySync, isStockTakeModuleBlocked, STOCK_TAKE_SYNC_GATE_ENABLED } from '../utils/stock-take-sync-gate.utils';
import { IStockTakeState } from '../models/stock-take-state.interface';
import {
  IStockItemApiData,
  IStockTake,
  IStockTakeFinalFigure,
  IStockTakeItem,
  IStockTakeItemDetail,
  StockTakeItemStatus,
  StockTakeStatus,
  STOCK_TAKE_LEGACY_PENDING_DOCUMENT_STATUS,
} from '../models';
import { buildStockTakePopulationProgress } from '../utils/stock-take-population-progress.utils';
import {
  calculateStockTakeItemVariance,
  calculateStockTakeRandVariance,
  isStockTakeIncludeDelCol,
} from '../utils/stock-take-variance.utils';

export const selectStockTakeState =
  createFeatureSelector<IStockTakeState>('stockTake');

export const selectLastStockTakeSyncStatus = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.lastStockTakeSyncStatus,
);

export const selectLastStockTakeSyncStatusForSelectedStore = createSelector(
  selectStockTakeState,
  selectSelectedStore,
  (state, selectedStore) => {
    if (!selectedStore?.storeId || state.lastStockTakeSyncStatusStoreId !== selectedStore.storeId) {
      return null;
    }
    return state.lastStockTakeSyncStatus;
  },
);

export const selectIsLastStockTakeSyncComplete = createSelector(
  selectLastStockTakeSyncStatusForSelectedStore,
  (status) => status === 'SUCCESS',
);

export const selectTypesenseMissingStockTakeDateCount = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.typesenseMissingStockTakeDateCount,
);

export const selectTypesenseStockTakeDateSchemaReady = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.typesenseStockTakeDateSchemaReady,
);

export const selectIsTypesenseStockTakeDateCheckLoading = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.isTypesenseStockTakeDateCheckLoading,
);

export const selectIsLastStockTakeSyncStatusLoading = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.isLastStockTakeSyncStatusLoading,
);

export const selectIsLastStockTakeSyncStatusKnownForStore = (
  storeId: string,
): MemoizedSelector<object, boolean> =>
  createSelector(
    selectStockTakeState,
    (state: IStockTakeState) =>
      state.lastStockTakeSyncStatusStoreId === storeId && state.lastStockTakeSyncStatus !== null,
  );

export const selectIsStockTakeSyncBlocked = createSelector(
  selectLastStockTakeSyncStatusForSelectedStore,
  (syncStatus) => isStockTakeBlockedBySync(syncStatus),
);

export const selectIsStockTakeSyncGateResolving = createSelector(
  selectStockTakeState,
  selectSelectedStore,
  (state, selectedStore) => {
    if (!STOCK_TAKE_SYNC_GATE_ENABLED || !selectedStore?.storeId) {
      return false;
    }
    if (state.lastStockTakeSyncStatusStoreId !== selectedStore.storeId) {
      return true;
    }
    return state.lastStockTakeSyncStatus === null;
  },
);

export const selectIsStockTakeModuleBlocked = createSelector(
  selectLastStockTakeSyncStatusForSelectedStore,
  selectTypesenseMissingStockTakeDateCount,
  selectTypesenseStockTakeDateSchemaReady,
  (syncStatus, missingCount, schemaReady) =>
    isStockTakeModuleBlocked(syncStatus, missingCount, schemaReady),
);

export const selectIsStockTakeModuleUnlocked = createSelector(
  selectIsStockTakeModuleBlocked,
  (isBlocked) => !isBlocked,
);

export const selectIsRequestingLastStockTakeSync = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.isRequestingSync,
);

export const selectLastStockTakeSyncItemsSynced = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.lastStockTakeSyncItemsSynced,
);

export const selectLastStockTakeSyncTotalItems = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.lastStockTakeSyncTotalItems,
);

export const selectLastStockTakeSyncError = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.lastStockTakeSyncError,
);

export const selectLastStockTakeSyncInfoMessage = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.lastStockTakeSyncInfoMessage,
);

export const selectLastStockTakeSyncObservationPath = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.lastStockTakeSyncObservationPath,
);

export const selectLastStockTakeSyncExecutedAt = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.lastStockTakeSyncExecutedAt,
);

export const selectLastStockTakeSyncProcessingStartedAt = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.lastStockTakeSyncProcessingStartedAt,
);

export const selectLastStockTakeSyncProgressPercent = createSelector(
  selectLastStockTakeSyncItemsSynced,
  selectLastStockTakeSyncTotalItems,
  selectLastStockTakeSyncStatus,
  (synced, total, status) => {
    if (status !== 'PENDING') {
      return null;
    }
    const syncedCount = synced ?? 0;
    if (total == null || total <= 0) {
      return syncedCount > 0 ? 1 : 0;
    }
    return Math.min(100, Math.round((syncedCount / total) * 100));
  },
);

export const selectStockTakeDashboardData = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.dashboardData,
);

export const selectIsStockTakeDashboardLoading = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.isDashboardLoading,
);

/**
 * Select all adjustments by store ID
 */
export const selectStockTakesByStore = (
  storeId: string,
): MemoizedSelector<object, IStockTake[]> =>
  createSelector(selectStockTakeState, (state: IStockTakeState) =>
    state.adjustments[storeId] || [],
  );

/**
 * Stock takes for list display — counts come from parent document fields on each IStockTake.
 */
export const selectStockTakesForDisplay = selectStockTakesByStore;

export const selectIsRunningSchedules = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.isRunningSchedules,
);

/**
 * Select current adjustment
 */
export const selectCurrentStockTake = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.currentAdjustment,
);

/**
 * Select items for current adjustment
 */
export const selectCurrentStockTakeItems = createSelector(
  selectStockTakeState,
  selectCurrentStockTake,
  (state: IStockTakeState, currentAdjustment) => {
    if (!currentAdjustment) return [];
    return state.adjustmentItems[currentAdjustment.id] || [];
  },
);

/**
 * Select items for a specific adjustment
 */
export const selectAdjustmentItems = (
  adjustmentId: string,
): MemoizedSelector<object, IStockTakeItem[]> =>
  createSelector(selectStockTakeState, (state: IStockTakeState) =>
    state.adjustmentItems[adjustmentId] || [],
  );

/**
 * Select active scan session
 */
export const selectActiveScanSession = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.activeScanSession,
);

/**
 * Select scanned items from active session
 */
export const selectScannedItems = createSelector(
  selectActiveScanSession,
  (session) => {
    if (!session) return [];
    return Object.values(session.scannedItems);
  },
);

/**
 * Select scanned item count
 */
export const selectScannedItemCount = createSelector(
  selectActiveScanSession,
  (session) => session?.itemCount || 0,
);

/**
 * Select total scanned quantity
 */
export const selectTotalScannedQuantity = createSelector(
  selectScannedItems,
  (items) => items.reduce((sum, item) => sum + item.count, 0),
);

/**
 * Select total scanned value (not available without cost data)
 */
export const selectTotalScannedValue = createSelector(
  selectScannedItems,
  () => 0,
);

/**
 * Select loading states
 */
export const selectIsStockTakeLoading = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.isAdjustmentsLoading,
);

export const selectIsAdjustmentDetailsLoading = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.isAdjustmentDetailsLoading,
);

export const selectIsProcessingAdjustment = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.isProcessingAdjustment,
);

export const selectIsCreatingStockTakeFromProcess = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.isCreatingStockTakeFromProcess,
);

export const selectCreateStockTakeFromProcessResult = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.createStockTakeFromProcessResult,
);

export const selectIsRetryingFailedStockTakeProcessing = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.isRetryingFailedStockTakeProcessing,
);

export const selectRetryFailedStockTakeProcessingOutcome = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.retryFailedStockTakeProcessingOutcome,
);

export const selectIsDeletingStockTake = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.isDeletingStockTake,
);

export const selectStockTakeStatusCounts = (storeId: string): MemoizedSelector<object, Record<string, number>> =>
  createSelector(
    selectStockTakeState,
    (state: IStockTakeState): Record<string, number> => {
      const counts = { ...(state.statusCounts[storeId] ?? {}) };
      const pending = state.pendingDeletionStatuses[storeId] ?? {};
      for (const status of Object.values(pending)) {
        counts[status] = Math.max(0, (counts[status] ?? 0) - 1);
      }
      return counts;
    },
  );

export const selectIsPopulatingStockTakeItems = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.isPopulatingStockTakeItems,
);

export const selectStockTakePopulationProgress = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.populationProgress,
);

export const selectStockTakePopulationLoadingPresentation = createSelector(
  selectIsPopulatingStockTakeItems,
  selectIsRunningSchedules,
  selectStockTakePopulationProgress,
  (isPopulating, isRunning, progress) => {
    if (!isPopulating && !isRunning) {
      return null;
    }
    if (progress) {
      return progress;
    }
    return buildStockTakePopulationProgress(
      1,
      isRunning ? 'Creating stock takes from schedule…' : 'Creating stock take…',
    );
  },
);

export const selectCompletedHasMore = (storeId: string): MemoizedSelector<object, boolean> =>
  createSelector(
    selectStockTakeState,
    (state: IStockTakeState): boolean => state.completedHasMore[storeId] ?? false,
  );

export const selectIsCompletedLoadingMore = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.isCompletedLoadingMore,
);

/**
 * Select status filter
 */
export const selectStatusFilter = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.statusFilter,
);

/**
 * Select item details loading state
 */
export const selectIsItemDetailsLoading = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.isItemDetailsLoading,
);

/**
 * Select the last time stock item details were fetched from Firestore
 */
export const selectLastItemDetailsFetchTime = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.lastItemDetailsFetchTime,
);

/**
 * Select stock take item codes that have no matching document in the shared stock collection
 */
export const selectMissingItemCodes = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.missingItemCodes,
);

/**
 * Whether Firestore stock file enrichment failed for a stock take (e.g. permission denied).
 */
export const selectIsItemDetailsLoadFailed = (
  adjustmentId: string,
): MemoizedSelector<object, boolean> =>
  createSelector(
    selectStockTakeState,
    (state: IStockTakeState) => state.itemDetailsLoadFailedByAdjustmentId[adjustmentId] ?? false,
  );

/**
 * True while orphaned stock-take lines are being removed from item_chunks
 */
export const selectIsRemovingMissingStockTakeItems = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.isRemovingMissingStockTakeItems,
);

/**
 * Select raw API data for all adjustments
 */
export const selectStockItemApiData = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.stockItemApiData,
);

/**
 * Select movement data for all adjustments
 */
export const selectStockMovementData = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.stockMovementData,
);

/**
 * Select whether movement data is currently loading
 */
export const selectIsMovementLoading = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.isMovementLoading,
);

/**
 * Select the timestamp of the last movement fetch attempt (null = never fetched)
 */
export const selectLastMovementFetchTime = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.lastMovementFetchTime,
);

/**
 * Select whether movement data failed to load (data may be stale)
 */
export const selectMovementDataOutOfSync = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.movementDataOutOfSync,
);

/**
 * Select enriched stock take items — combines Firestore counts with API data
 * and computes variance and randVariance on the frontend
 */
export const selectEnrichedStockTakeItems = (
  adjustmentId: string,
): MemoizedSelector<object, IStockTakeItemDetail[]> =>
  createSelector(
    selectAdjustmentItems(adjustmentId),
    selectStockItemApiData,
    selectStockMovementData,
    selectCurrentStockTake,
    (
      items: IStockTakeItem[],
      allApiData: Record<string, Record<string, IStockItemApiData>>,
      allMovementData: Record<string, Record<string, { stockMovement: number; delColMovement: number }>>,
      currentStockTake: IStockTake | null,
    ) => {
      const apiData = allApiData[adjustmentId] ?? {};
      const movementData = allMovementData[adjustmentId] ?? {};
      const includeDelCol = isStockTakeIncludeDelCol(
        currentStockTake?.id === adjustmentId ? currentStockTake.includeDelCol : true,
      );
      return items.map((item: IStockTakeItem): IStockTakeItemDetail => {
        const detail = apiData[item.code];
        const movement = movementData[item.code];
        const onhand = detail?.onhand ?? 0;
        const rawDelColQty = detail?.delColMovement ?? 0;
        const stockMovement = movement?.stockMovement ?? 0;
        const rawDelColMovement = movement?.delColMovement ?? 0;
        const delColQty = includeDelCol ? rawDelColQty : 0;
        const delColMovement = includeDelCol ? rawDelColMovement : 0;
        const latestCost = detail?.latestCost ?? 0;
        const avCost = detail?.avCost ?? 0;
        const variance = calculateStockTakeItemVariance({
          qtyCounted: item.qtyCounted,
          onhand,
          delColQty,
          delColMovement,
          movement: stockMovement,
          includeDelCol,
        });
        return {
          id: item.id,
          code: item.code,
          qtyCounted: item.qtyCounted,
          previousQtyCounted: item.previousQtyCounted ?? 0,
          timesCounted: item.timesCounted,
          status: item.status ?? StockTakeItemStatus.pending,
          description: detail?.description ?? '',
          onhand,
          delColQty,
          movement: stockMovement,
          delColMovement,
          latestCost,
          variance,
          randVariance: calculateStockTakeRandVariance(variance, avCost),
          countedByEmail: item.countedByEmail,
          binLocation: item.binLocation,
          barcode: detail?.barcode,
          suppCode: detail?.suppCode,
          regularSuppCode: detail?.regularSuppCode,
          binL: detail?.binL,
          packSize: detail?.packSize,
          avCost: detail?.avCost,
          sellPriIncl1: detail?.sellPriIncl1,
          sellPriExcl1: detail?.sellPriExcl1,
          dep: detail?.dep,
          subDep: detail?.subDep,
          gp1: detail?.gp1,
          nominalGP: detail?.nominalGP,
          onHoldCode: detail?.onHoldCode,
          ordLvl: detail?.ordLvl,
          orderQuantity: detail?.orderQuantity,
          unitsYear: detail?.unitsYear,
          lastSold: detail?.lastSold,
          created: detail?.created,
          genCode: detail?.genCode,
          category: detail?.category,
          purchaseOrder: detail?.purchaseOrder,
          vatR: detail?.vatR,
          lineColourCode: detail?.lineColourCode,
          lastStockTake: detail?.lastStockTake,
          pendingBarcodeUpdate: item.pendingBarcodeUpdate,
          reportItemCode: detail?.reportItemCode,
          reportItemFactor: detail?.reportItemFactor,
          noDecimal: detail?.noDecimal,
        };
      });
    },
  );

export const selectStockTakeItemMutationCount = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.stockTakeItemMutationCount,
);

export const selectIsStockTakeItemMutationInProgress = createSelector(
  selectStockTakeItemMutationCount,
  (count: number) => count > 0,
);

export const selectStockTakeItemsListenerSettling = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.stockTakeItemsListenerSettling,
);

/**
 * True while Firestore is settling after a user item edit — not background enrichment.
 */
export const selectIsStockTakeItemMutationBusy = createSelector(
  selectIsStockTakeItemMutationInProgress,
  selectStockTakeItemsListenerSettling,
  (mutationInProgress, listenerSettling) => mutationInProgress || listenerSettling,
);

/**
 * True while the items grid should stay covered: Firestore sync, product details, or movement fetch.
 */
export const selectIsStockTakeItemsGridBusy = createSelector(
  selectIsStockTakeItemMutationInProgress,
  selectIsItemDetailsLoading,
  selectIsMovementLoading,
  selectStockTakeItemsListenerSettling,
  (mutationInProgress, itemDetailsLoading, movementLoading, listenerSettling) =>
    mutationInProgress || itemDetailsLoading || movementLoading || listenerSettling,
);

export const selectIsCountingAllPendingAsZero = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.isCountingAllPendingAsZero,
);

export const selectIsRevertingStockTakeToInProgress = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.isRevertingStockTakeToInProgress,
);

export const selectIsAssigningBarcode = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.isAssigningBarcode,
);

export const selectIsStockTakeItemsLoading = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.isStockTakeItemsLoading,
);

/**
 * Select the transactions timestamp from data_timestamps
 */
export const selectTransactionsTimestamp = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.transactionsTimestamp,
);

/**
 * Select the delcolissuestran timestamp from data_timestamps
 */
export const selectDelColIssuesTransTimestamp = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.delColIssuesTransTimestamp,
);

/**
 * Select the stock data timestamp from data_timestamps
 */
export const selectStockTimestamp = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.stockTimestamp,
);

/**
 * Select the barcode code pending quantity entry in the scan item modal
 */
export const selectPendingScannedCode = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.pendingScannedCode,
);

/**
 * Select a scanned barcode that could not be resolved to a stock item
 */
export const selectBarcodeNotFoundBarcode = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.barcodeNotFoundBarcode,
);

/**
 * Select errors
 */
export const selectStockTakeErrors = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.errors,
);

/**
 * Select open (non-terminal) stock takes for a store — used for recount target selection
 */
export const selectOpenStockTakesForStore = (
  storeId: string,
): MemoizedSelector<object, IStockTake[]> =>
  createSelector(selectStockTakesByStore(storeId), (adjustments) =>
    adjustments.filter(
      (adj) =>
        adj.status === StockTakeStatus.inProgress ||
        adj.status === STOCK_TAKE_LEGACY_PENDING_DOCUMENT_STATUS ||
        adj.status === StockTakeStatus.prepared,
    ),
  );

/**
 * Select filtered adjustments by status
 */
export const selectFilteredAdjustments = (
  storeId: string,
): MemoizedSelector<object, IStockTake[]> =>
  createSelector(
    selectStockTakesByStore(storeId),
    selectStatusFilter,
    (adjustments, statusFilter) => {
      if (!statusFilter) return adjustments;
      return adjustments.filter((adj) => adj.status === statusFilter);
    },
  );

/**
 * Select in progress adjustments count
 */
export const selectInProgressAdjustmentsCount = (
  storeId: string,
): MemoizedSelector<object, number> =>
  createSelector(selectStockTakesByStore(storeId), (adjustments) =>
    adjustments.filter(
      (adj) =>
        adj.status === StockTakeStatus.inProgress ||
        adj.status === STOCK_TAKE_LEGACY_PENDING_DOCUMENT_STATUS,
    ).length,
  );

/**
 * Select submitted adjustments count
 */
export const selectSubmittedAdjustmentsCount = (
  storeId: string,
): MemoizedSelector<object, number> =>
  createSelector(selectStockTakesByStore(storeId), (adjustments) =>
    adjustments.filter((adj) => adj.status === StockTakeStatus.submitted).length,
  );

/**
 * Select finalised item snapshots for a specific adjustment
 */
export const selectFinalisedItems = (
  adjustmentId: string,
): MemoizedSelector<object, IStockTakeFinalFigure[]> =>
  createSelector(
    selectStockTakeState,
    (state: IStockTakeState) => state.finalisedItems[adjustmentId] ?? [],
  );

/**
 * Select whether finalised items are currently loading
 */
export const selectIsFinalisedItemsLoading = createSelector(
  selectStockTakeState,
  (state: IStockTakeState) => state.isFinalisedItemsLoading,
);
